import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useOrganization } from "@/contexts/organization-context"
import { Button } from "@/components/ui/button"
import { organizationService } from "@/services/organizations"
import { OrganizationType } from "@/types/organization"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import { Plus, Copy, Eye, EyeOff, ExternalLink } from "lucide-react"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

export function OrganizationsPage() {
  const navigate = useNavigate()
  const { organizations, loading, refreshOrganizations } = useOrganization()
  const [showApiKey, setShowApiKey] = useState<{ [key: string]: boolean }>({})
  const [declineDialog, setDeclineDialog] = useState<{
    open: boolean
    organization?: OrganizationType
    token?: string
  }>({ open: false })
  const [showEnabledOnly, setShowEnabledOnly] = useState(true)

  // Refresh organizations when page loads
  useEffect(() => {
    refreshOrganizations()
  }, [])

  const handleSwitchOrganization = (slug: string) => {
    if (!slug) {
      toast.error("Cannot switch to organization without slug")
      return
    }

    // Try to go back to the previous page with the new org slug
    const previousPath = sessionStorage.getItem('previousOrgPath')
    if (previousPath) {
      // Replace the org slug in the previous path
      const pathParts = previousPath.split('/')
      if (pathParts.length >= 2) {
        pathParts[1] = slug
        sessionStorage.removeItem('previousOrgPath')
        navigate(pathParts.join('/'))
        return
      }
    }

    // Default to optimizer page if no previous path
    navigate(`/${slug}/optimizer`)
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast.success(`${label} copied to clipboard`)
  }

  const toggleApiKeyVisibility = (orgId: number) => {
    setShowApiKey(prev => ({
      ...prev,
      [orgId]: !prev[orgId]
    }))
  }

  const acceptInvitation = async (token: string) => {
    try {
      await organizationService.acceptInvitation(token)
      
      // Refresh the organizations list after accepting
      await refreshOrganizations()
      
      toast.success("Invitation accepted successfully!")
    } catch (error) {
      toast.error("Error accepting invitation")
      console.error("Error accepting invitation:", error)
    }
  }

  const declineInvitation = async () => {
    try {
      if (declineDialog.token && declineDialog.organization) {
        await organizationService.declineInvitation(declineDialog.token)
        
        // Refresh the organizations list after declining
        await refreshOrganizations()
      }
      
      setDeclineDialog({ open: false })
      toast.success("Invitation declined successfully!")
    } catch (error) {
      toast.error("Error declining invitation")
      console.error("Error declining invitation:", error)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Organizations</h2>
          <p className="text-muted-foreground">
            Manage and switch between your organizations
          </p>
        </div>
        <Button variant="outline" size="default" onClick={() => navigate("/organizations/new")}>
          <Plus className="mr-2 h-4 w-4" />
          New Organization
        </Button>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>API Key</TableHead>
              <TableHead className="text-center">
                <div className="flex items-center justify-center gap-2">
                  <Switch
                    checked={showEnabledOnly}
                    onCheckedChange={setShowEnabledOnly}
                    onClick={(e) => e.stopPropagation()}
                    className="scale-50"
                  />
                  <span>Status</span>
                </div>
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Loading organizations...
                </TableCell>
              </TableRow>
            ) : organizations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No organizations found
                </TableCell>
              </TableRow>
            ) : (
              // Sort organizations alphabetically by name and filter by enabled status
              [...organizations]
                .filter(org => showEnabledOnly ? org.status === 1 : true)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((org) => (
              <TableRow key={org.id} className={org.accepted ? "cursor-pointer" : ""}>
                <TableCell>{org.id}</TableCell>
                <TableCell 
                  className="font-bold"
                  onClick={() => org.accepted && handleSwitchOrganization(org.slug)}
                >
                  {org.name}
                </TableCell>
                <TableCell>
                  {org.slug ? (
                    <div className="flex items-center gap-2">
                      <code className="text-sm">{org.slug}</code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation()
                          copyToClipboard(org.slug, "Slug")
                        }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">No slug</span>
                  )}
                </TableCell>
                <TableCell>
                  {org.accepted && org.api_key ? (
                    <div className="flex items-center gap-2">
                      <code className="text-sm">
                        {showApiKey[org.id] 
                          ? org.api_key 
                          : `sk_live_${"•".repeat(20)}`}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleApiKeyVisibility(org.id)
                        }}
                      >
                        {showApiKey[org.id] ? (
                          <EyeOff className="h-3 w-3" />
                        ) : (
                          <Eye className="h-3 w-3" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation()
                          copyToClipboard(org.api_key, "API Key")
                        }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {org.status !== undefined && org.status !== null ? (
                    <Badge 
                      variant={org.status === 1 ? 'default' : 'secondary'}
                    >
                      {org.status === 1 ? 'Enabled' : 'Disabled'}
                    </Badge>
                  ) : (
                    <Badge variant="outline">Unknown</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {org.accepted ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleSwitchOrganization(org.slug)
                      }}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Open
                    </Button>
                  ) : (
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => acceptInvitation(org.accept_token!)}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeclineDialog({
                          open: true,
                          organization: org,
                          token: org.accept_token
                        })}
                      >
                        Decline
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={declineDialog.open} onOpenChange={(open) => 
        setDeclineDialog({ ...declineDialog, open })
      }>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Invitation</DialogTitle>
            <DialogDescription>
              Are you sure you want to decline the invitation to {declineDialog.organization?.name}? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeclineDialog({ open: false })}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={declineInvitation}
            >
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}